import { useCallback, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { NotificationService } from 'ui-components/services/notification-service';

import { Segment } from '../../../../../services/analytics-services/segment';
import { useMarketplaceDisplayNameFromStoreGuid } from '../../../../../hooks/use-marketplace-display-name-from-store-guid';
import { DropdownActionsContext } from './dropdown-actions-context';
import { printLabel } from '../../../../../store/printing/print.actions';
import { cancelRequest } from '../../../../../store/returns/returns.api';

import { messages } from './messages';
import {
  RETURN_ACTION_CANCEL_CLICKED,
  RETURN_ACTION_PRINT_LABEL_CLICKED,
  RETURN_ACTION_VIEW_MARKETPLACE_CLICKED,
} from '../../../../../constants/analytics-events';
import type { ActionProps } from './dropdown-actions-types';

export const PrintLabelAction = ({ data, storeGuid }: ActionProps) => {
  const dispatch = useDispatch();

  const onPrintLabel = useCallback(() => {
    Segment.trackEvent(RETURN_ACTION_PRINT_LABEL_CLICKED, {
      type: RETURN_ACTION_PRINT_LABEL_CLICKED,
      enhanced: true,
      requestId: data.id,
      storeGuid,
      fulfillmentId: data.fulfillmentId,
    });
    dispatch(printLabel([], [data.fulfillmentId]));
  }, [data.fulfillmentId, data.id, dispatch, storeGuid]);

  return (
    <DropdownMenuItem onClick={onPrintLabel}>
      <FormattedMessage {...messages.printLabelText} />
    </DropdownMenuItem>
  );
};

export const SendLabelAction = ({ data }: ActionProps) => {
  const { openModal } = useContext(DropdownActionsContext);

  const onClick = useCallback(() => openModal(data), [data, openModal]);

  return (
    <DropdownMenuItem onClick={onClick}>
      <FormattedMessage {...messages.sendReturnLabelText} />
    </DropdownMenuItem>
  );
};

export const ViewOnMarketplaceAction = ({ data, storeGuid }: ActionProps) => {
  const marketplaceName = useMarketplaceDisplayNameFromStoreGuid(storeGuid);
  const url = data.orderInfo.ecommerceOrderUrl;

  const trackEvent = useCallback(async () => {
    Segment.trackEvent(RETURN_ACTION_VIEW_MARKETPLACE_CLICKED, {
      type: RETURN_ACTION_VIEW_MARKETPLACE_CLICKED,
      enhanced: true,
      requestId: data.id,
      storeGuid,
      marketplaceName,
      url,
    });
  }, [data.id, storeGuid, marketplaceName, url]);

  return (
    <DropdownMenuItem href={url} onClick={trackEvent} target='_self'>
      <FormattedMessage {...messages.viewOnMarketplaceText} values={{ marketplaceName }} />
    </DropdownMenuItem>
  );
};

export const CancelAction = ({ data, storeGuid, onReload }: ActionProps) => {
  const shippingLabel = data.shippingInfo.shipmentLabel;

  const onCancel = useCallback(async () => {
    try {
      Segment.trackEvent(RETURN_ACTION_CANCEL_CLICKED, {
        type: RETURN_ACTION_CANCEL_CLICKED,
        enhanced: true,
        requestId: data.id,
        storeGuid,
      });
      await cancelRequest(data.id, storeGuid);
      onReload();
      NotificationService.showGenericSuccessNotification(<FormattedMessage {...messages.cancelSuccessTitle} />);
    } catch {
      NotificationService.showGenericFailureNotification();
    }
  }, [data.id, onReload, storeGuid]);

  return (
    <DropdownMenuItem onClick={onCancel}>
      <FormattedMessage {...messages.cancelRequestText} values={{ hasLabel: !!shippingLabel }} />
    </DropdownMenuItem>
  );
};
